import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlinePrinter } from 'react-icons/ai';
import { MdAttachMoney } from 'react-icons/md';

import { ArrowCircleBackground } from '../../../../assets/images/icons/arrow-circle-background';
import {
  DataTable,
  StyledButton,
  StyledDateInput,
  StyledSelect,
} from '../../../../components';
import { ExportButton } from '../../../../components/ExportButton';
import { useFilters } from '../../../../services/contexts/FiltersContext';
import { useUserDocument } from '../../../../services/contexts/UserDocumentContext';
import { useTransacaoParcela } from '../../../../services/hooks/useTransacaoParcela';
import { PrintComponent } from '../../../../services/PrintComponent';
import { CnpjMask } from '../../../../utils/CnpjMask';
import { CpfMask } from '../../../../utils/CpfMask';
import { ThityDaysFromNow } from '../../../../utils/ThityDaysFromNow';
import { AdminTitle } from '../../components';
import { Container, FiltersContainer } from './styles';
import { InputDescription } from '../../components/InputDescription';

const tableHead = [
  'Data e Hora',
  'NSU',
  'Autorização',
  'Cartão',
  'Terminal',
  'Situação',
  'Produto',
  'Qtd. Parcelas',
  'Parcela',
  'Vl. Parcela',
  'Vl. Tx. Adm.',
  'Vl. Tx. Ant.',
  'Vl. Líquido',
  'Prev. Pgto',
  'Vencto Orig.',
  'Documento',
  'Nome Fantasia',
];

export const TransacoesParcelas = () => {
  const { documentSelected } = useUserDocument();
  const {
    periodType,
    setPeriodType,
    initialDate,
    setInitialDate,
    finalDate,
    setFinalDate,
    productType,
    setProductType,
    flag,
    setFlag,
    cardFlagsOptions,
  } = useFilters();

  const docto = useMemo(() => documentSelected as string, [documentSelected]);

  const today = new Date();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [isOpenFinalDate, setIsOpenFinalDate] = useState(false);

  const [print, setPrint] = useState(false);

  const [params, setParams] = useState({
    docto: '',
    dtInicial: initialDate
      ?.toLocaleDateString('pt-BR')
      .split('/')
      .reverse()
      .join('-'),
    dtFinal: finalDate
      ?.toLocaleDateString('pt-BR')
      .split('/')
      .reverse()
      .join('-'),
    tipoPeriodo: periodType,
    bandeira: flag,
    tipoProd: productType,
    pagina: page,
    limite: itemsPerPage,
  });

  useEffect(() => {
    if (docto) {
      setParams((previousState) => ({ ...previousState, docto: docto }));
    }
  }, [docto]);

  const { data, isLoading, isFetching, error, refetch } = useTransacaoParcela({
    ...params,
    pagina: page,
    limite: itemsPerPage,
  });

  const onChangeSetInitialDate = (date: any) => {
    setInitialDate(date);
    setIsOpenFinalDate(true);
    setFinalDate(ThityDaysFromNow(date));
  };

  const onChangeSetFinalDate = (date: any) => {
    setFinalDate(date);
    setIsOpenFinalDate(false);
  };

  const handleFilter = useCallback(() => {
    const newParams = {
      docto: docto,
      dtInicial: initialDate
        ?.toLocaleDateString('pt-BR')
        .split('/')
        .reverse()
        .join('-'),
      dtFinal: finalDate
        ?.toLocaleDateString('pt-BR')
        .split('/')
        .reverse()
        .join('-'),
      pagina: page,
      limite: itemsPerPage,
      tipoPeriodo: periodType,
      bandeira: flag,
      tipoProd: productType,
    };

    setParams(newParams);
    setPage(1);
    setItemsPerPage(10);
    refetch();
  }, [
    docto,
    initialDate,
    finalDate,
    page,
    itemsPerPage,
    periodType,
    flag,
    productType,
    refetch,
  ]);

  useEffect(() => {
    if (periodType === 'P') {
      setPeriodType('');
    }
  }, []);

  useEffect(() => {
    setPage(1);
  }, [itemsPerPage]);

  return (
    <Container>
      <AdminTitle title="Transações Parcelas" icon={<MdAttachMoney />} />

      <FiltersContainer>
        <div>
          <InputDescription placeholder="Filtro por período" />
          <StyledSelect
            placeholder="Tipo de Período"
            options={[
              { id: 'E', value: 'Emissão' },
              { id: 'V', value: 'Vencimento' },
            ]}
            setValue={setPeriodType}
            value={periodType}
          />
        </div>

        <div>
          <InputDescription placeholder="Data inicial" />
          <StyledDateInput
            value={initialDate}
            onChange={onChangeSetInitialDate}
            placeholder="Data inicial"
          />
        </div>

        <div>
          <InputDescription placeholder="Data final" />
          <StyledDateInput
            value={finalDate}
            onChange={onChangeSetFinalDate}
            placeholder="Data final"
            minDate={initialDate || undefined}
            maxDate={initialDate ? ThityDaysFromNow(initialDate) : today}
            isOpen={isOpenFinalDate}
            onCalendarClose={() => setIsOpenFinalDate(false)}
          />
        </div>

        <div>
          <InputDescription placeholder="Filtro por produto" />
          <StyledSelect
            placeholder="Tipo de Produto"
            options={[
              { id: 'DB', value: 'Débito' },
              { id: 'CV', value: 'Crédito à Vista' },
              { id: 'CP', value: 'Crédito Parcelado' },
              { id: 'PX', value: 'Pix' },
            ]}
            setValue={setProductType}
            value={productType}
          />
        </div>

        <div>
          <InputDescription placeholder="Filtro por bandeira" />
          <StyledSelect
            placeholder="Bandeira"
            options={cardFlagsOptions}
            setValue={setFlag}
            value={flag}
          />
        </div>

        <StyledButton
          text="Consultar"
          icon={<ArrowCircleBackground />}
          iconPosition="right"
          onClick={handleFilter}
        />

        <ExportButton
          params={params}
          transactionType="transacaoParcela"
          disabled={isLoading || isFetching}
        />

        <StyledButton
          text="Imprimir"
          icon={<AiOutlinePrinter />}
          iconPosition="right"
          onClick={() => setPrint(true)}
          disabled={isLoading || isFetching}
        />
      </FiltersContainer>

      <PrintComponent onPrint={print} setOnPrint={setPrint}>
        <DataTable
          title="Detalhado"
          heads={tableHead}
          hasPagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          totalItems={data?.total}
        >
          {isLoading ? (
            <tbody>
              <tr>
                <td colSpan={17}>Carregando...</td>
              </tr>
            </tbody>
          ) : error ? (
            <tbody>
              <tr>
                <td colSpan={17}>Falha ao obter dados</td>
              </tr>
            </tbody>
          ) : data?.transactions.length === 0 ? (
            <tr>
              <td colSpan={17}>Não foram encontrados dados</td>
            </tr>
          ) : (
            <tbody>
              {data?.transactions?.map((transacao) => (
                <tr key={transacao?.nsuTransacao}>
                  <td>
                    {new Date(transacao?.dtHrTransacao).toLocaleString('pt-BR')}
                  </td>
                  <td>{transacao?.nsuTransacao}</td>
                  <td>{transacao?.codAutTransacao}</td>
                  <td>{`${transacao?.cartaoBIN?.slice(
                    0,
                    4
                  )} ${transacao?.cartaoBIN?.slice(4)}XX XXXX ${transacao?.cartaoHolder
                    }`}</td>
                  <td>{transacao?.numSerieLeitor}</td>
                  <td>{transacao?.situacao === 'A' ? 'Ativo' : 'Cancelado'}</td>
                  <td>{transacao?.descricao}</td>
                  <td>{transacao?.qtdParcela}</td>
                  <td>{transacao?.numParcela}</td>
                  <td>
                    {transacao?.vlParcela?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {transacao?.vlTxAdm?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {transacao?.vlTxAnt?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {transacao?.vlLiquido?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {new Date(transacao?.dtPrevPgto)?.toLocaleDateString(
                      'pt-BR'
                    )}
                  </td>
                  <td>
                    {new Date(transacao?.dtVenctoOriginal)?.toLocaleDateString(
                      'pt-BR'
                    )}
                  </td>
                  <td>
                    {transacao.estabDocto?.length === 14
                      ? CnpjMask(transacao.estabDocto)
                      : CpfMask(transacao.estabDocto)}
                  </td>
                  <td>{transacao.estabFantasia}</td>
                </tr>
              ))}
            </tbody>
          )}
        </DataTable>
      </PrintComponent>
    </Container>
  );
};
